import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

export interface CookieConsent {
  analytics: boolean
  functional: boolean
  necessary: boolean
  savedAt: number
}

interface CookieConsentContextValue {
  consent: CookieConsent | null
  updateConsent: (analytics: boolean, functional: boolean) => void
  hasConsent: boolean
}

const CookieConsentContext = createContext<
  CookieConsentContextValue | undefined
>(undefined)

const STORAGE_KEY = 'cleverlink_cookie_consent'

export const CookieConsentProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [consent, setConsent] = useState<CookieConsent | null>(null)

  // Load consent from localStorage on mount
  useEffect(() => {
    const loadConsent = () => {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          setConsent(JSON.parse(stored))
        } catch (e) {
          console.error('Failed to parse stored consent:', e)
        }
      }
    }

    loadConsent()
  }, [])

  const updateConsent = (analytics: boolean, functional: boolean) => {
    const newConsent: CookieConsent = {
      analytics,
      functional,
      necessary: true,
      savedAt: Date.now(),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newConsent))
    setConsent(newConsent)
  }

  return (
    <CookieConsentContext.Provider
      value={{ consent, updateConsent, hasConsent: consent !== null }}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext)
  if (context === undefined) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    )
  }

  return context
}
